import { MetricValueMode } from '@statsbomb/parachute-types';
import { atom, Atom } from 'jotai';
import { unwrappedMetricLookupMapAtom } from '@/atoms/queries/metric';
import { unwrappedTableColumnsAtom } from '@/atoms/queries/tableColumns';
import { metricValueModeAtom, unwrappedVisibleTableColumnsAtom } from '@/atoms/table';
import { MetricLookupMap } from '@/types/metric';
import { NestedObject } from '@/types/object';
import { DataTableColumn } from '@/types/table';
import { CellDataValue, EnrichedTableRow } from '@/utils/atoms/enrichTableData';
import { getDataKeyFromColumnKey, getMetricKeyFromColumnKey } from '@/utils/metrics';
import { getFormatRule, isMetricColumn } from '@/utils/table';

const RANK_PREFIX = 'rank';

const getRankForColumn = (
  columnKey: string,
  row: NestedObject,
  metricLookupMap: MetricLookupMap,
  metricValueMode: MetricValueMode,
): number | null => {
  if (!isMetricColumn(columnKey)) return null;

  const includePrefix = false;
  const metricKey = getDataKeyFromColumnKey(columnKey, metricLookupMap, metricValueMode, includePrefix);
  const rankKey = `${RANK_PREFIX}.${metricKey}`;
  const rankValue = row[rankKey];
  return typeof rankValue === 'number' ? rankValue : null;
};

const enrichWithValueAndFormat = (
  columnKey: string,
  row: NestedObject,
  availableColumns: DataTableColumn[],
  metricLookupMap: MetricLookupMap,
  metricValueMode: MetricValueMode,
): [string, EnrichedTableRow[string]] => {
  const metricKey = isMetricColumn(columnKey) ? getMetricKeyFromColumnKey(columnKey) : undefined;

  let value: CellDataValue;
  let keyUsedForValue: string;

  if (metricKey && row[metricKey]) {
    value = row[metricKey];
    keyUsedForValue = metricKey;
  } else if (row[columnKey]) {
    value = row[columnKey];
    keyUsedForValue = columnKey;
  } else {
    const dataKey = getDataKeyFromColumnKey(columnKey, metricLookupMap, metricValueMode);
    value = row[dataKey];
    keyUsedForValue = dataKey;
  }

  const formatRule = getFormatRule(keyUsedForValue, availableColumns) || 'string';
  const rank = getRankForColumn(columnKey, row, metricLookupMap, metricValueMode);
  return [columnKey, { value, formatRule, rank }];
};

export const createEnrichedDataAtom = (
  dataAtom: Atom<NestedObject[] | undefined>,
  entityKey: string,
): Atom<EnrichedTableRow[] | undefined> =>
  atom(get => {
    const data = get(dataAtom);
    if (!data) return data;

    const metricValueMode = get(metricValueModeAtom);
    const visibleColumns = get(unwrappedVisibleTableColumnsAtom);
    const metricLookupMap = get(unwrappedMetricLookupMapAtom);
    const tableColumns = get(unwrappedTableColumnsAtom);

    if (!tableColumns.length || !visibleColumns.length || !metricLookupMap) {
      return undefined;
    }

    const tooltipColumns = ['aggregates.minutesPlayed', 'aggregates.minutesPlayed360'];
    const essentialColumns = [...visibleColumns, entityKey, ...tooltipColumns];

    return data.map(row =>
      Object.fromEntries(
        essentialColumns.map(column =>
          enrichWithValueAndFormat(column, row, tableColumns, metricLookupMap, metricValueMode),
        ),
      ),
    );
  });
